// Build 93b — Tag-Input med autocomplete-pills.
// Build 96.8 — hela tagg-blocket ligger nu i ett collapsible <details>,
// grundläge är stängt så formulären ser mer luftiga ut. Förslags-pills är
// alphabetiskt sorterade med svensk collation (Å, Ä, Ö efter Z).
// Ingen datamigration, inga taggar ändras — endast presentation.
//
// Anvendelse:
//   <TagInput value={editData.taggar || ""} onChange={(s) => setEditData(p => ({...p, taggar: s}))} />
// value är komma-separerad sträng (kompatibel med befintlig serializeForApi / hydrateForUi).
import { useEffect, useState, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build 96.8 — svensk collation: A–Z, Å, Ä, Ö efter Z.
const SV_COLLATOR = new Intl.Collator("sv", { sensitivity: "base", numeric: true });
const sortSv = (arr) => [...arr].sort(SV_COLLATOR.compare);

function parseList(str) {
  return (str || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinList(arr) {
  return arr.join(", ");
}

export default function TagInput({ value, onChange, testid = "tag-input" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [used, setUsed] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/karttipps/tags`)
      .then((r) => {
        if (cancelled) return;
        setSuggestions(r.data?.suggested || []);
        setUsed(r.data?.used || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const current = useMemo(() => parseList(value), [value]);
  const currentSet = useMemo(() => new Set(current.map((c) => c.toLowerCase())), [current]);

  // Build 96.8 — alphabetiskt (svensk collation) i stället för prioritering.
  const available = useMemo(() => {
    const seen = new Set(currentSet);
    const out = [];
    const all = [...suggestions, ...used];
    for (const t of all) {
      const k = t.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
    }
    return sortSv(out);
  }, [suggestions, used, currentSet]);

  // Aktuella tags sorteras också alphabetiskt för att undvika visuellt brus.
  const currentSorted = useMemo(() => sortSv(current), [current]);

  const addTag = (t) => {
    if (!t || currentSet.has(t.toLowerCase())) return;
    onChange(joinList([...current, t]));
  };

  const removeTag = (t) => {
    onChange(joinList(current.filter((c) => c.toLowerCase() !== t.toLowerCase())));
  };

  const count = current.length;

  return (
    <details
      className="group rounded border border-warm-border bg-bone-alt/20 open:bg-white open:shadow-sm"
      data-testid={testid}
    >
      <summary
        className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-bark hover:text-ink flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden"
        data-testid={`${testid}-toggle`}
      >
        <span className="inline-block transition-transform group-open:rotate-90 text-bark/60">▸</span>
        <span>Taggar – visa/dölj</span>
        {count > 0 && (
          <span
            className="ml-1 inline-flex items-center rounded-full bg-pine/10 text-pine text-[11px] font-semibold px-2 py-0.5 border border-pine/20"
            data-testid={`${testid}-count`}
          >
            {count} vald{count === 1 ? "" : "a"}
          </span>
        )}
      </summary>

      <div className="px-3 pb-3 pt-1 space-y-2">
        {/* Free-text input — användaren kan alltid skriva egna tags */}
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="t.ex. Lokal mat, Vandring, Foto — eller klicka förslagen nedan"
          className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white"
          data-testid={`${testid}-input`}
        />

        {/* Current tags as removable pills (alphabetiskt) */}
        {currentSorted.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-current`}>
            {currentSorted.map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => removeTag(t)}
                className="inline-flex items-center gap-1 text-xs bg-pine/10 text-pine px-2 py-1 rounded-full border border-pine/20 hover:bg-pine/20"
                data-testid={`${testid}-remove-${t}`}
                title={`Ta bort #${t}`}
              >
                <span>#{t}</span>
                <span className="text-pine/60">×</span>
              </button>
            ))}
          </div>
        )}

        {/* Available suggestions (alphabetiskt A–Ö, svensk collation) */}
        {available.length > 0 && (
          <div data-testid={`${testid}-suggestions`}>
            <p className="text-[11px] uppercase tracking-wider text-bark/55 mb-1">Förslag (A–Ö, klicka för att lägga till)</p>
            <div className="flex flex-wrap gap-1.5">
              {available.map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => addTag(t)}
                  className="text-xs text-bark/75 bg-bone-alt border border-warm-border/60 px-2 py-1 rounded-full hover:bg-bone-alt/80 hover:text-pine"
                  data-testid={`${testid}-suggest-${t}`}
                  title={`Lägg till #${t}`}
                >
                  +#{t}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </details>
  );
}
